import { UI, Stack, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { getHomeValueVisibility } from '../../../../../../store/home/capture/selector';
import { customersHabilitation } from '../../../../../../store/home/conversion/selector';
import { PerformanceSummaryMetric } from '../../../PerformanceSummaryMetric';

export interface QualificationsContentProps {
  isMobile?: boolean;
}

export const QualificationsContent: React.FC<QualificationsContentProps> = ({ isMobile }) => {
  const valueVisible = useRecoilValue(getHomeValueVisibility);

  const {
    data: customersHabilitationData,
    loading: customersHabilitationLoading,
    refresher: refresherCustomersHabilitation,
    error: errorCustomersHabilitation,
  } = useRecoilQuery(customersHabilitation);

  const charContent = useMemo(() => {
    const bars = [
      {
        color: 'brand.secondary.dark',
        label: 'Qtd. habilitações',
        dataKey: 'habilitationValue',
      } as const,
    ];
    const additionalData = {
      year: customersHabilitationData?.data?.performanceYear ?? 2024,
      tooltipLabels: [...bars],
    };

    if (errorCustomersHabilitation) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ErrorBoundary
            onRefresh={refresherCustomersHabilitation}
            refreshText="RECARREGAR"
            refreshButtonCustomProps={{
              variant: 'textLink',
              rightIcon: (
                <UI.Icon name="Refresh" size="xxs" color={Color['brand.tertiary.principal']} />
              ),
            }}
          />
        </Stack>
      );
    }

    if (customersHabilitationLoading) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ChartBarsLoading isMobile={isMobile} barsLength={12} barWidth="40px" />
        </Stack>
      );
    }

    return (
      <UI.AccountQualificationsChart
        data={customersHabilitationData?.data.performanceData ?? []}
        additionalData={additionalData}
        bars={bars}
        isMobile={isMobile}
      />
    );
  }, [
    customersHabilitationData,
    customersHabilitationLoading,
    refresherCustomersHabilitation,
    isMobile,
    errorCustomersHabilitation,
  ]);

  const bigNumbers = customersHabilitationData?.data.bigNumbers;

  return (
    <VStack space={Spacing['stack.xxs']} direction={isMobile ? 'column-reverse' : 'column'}>
      <Stack
        space={Spacing['stack.xxs']}
        justifyContent="space-between"
        px={Spacing['inline.xxs']}
        direction={isMobile ? 'column' : 'row'}>
        <PerformanceSummaryMetric
          label="YTD"
          value={bigNumbers?.yearToDateValue ?? 0}
          performanceComparisonLabel="YoY"
          performanceComparisonValue={bigNumbers?.previousYearValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.yoYPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersHabilitationLoading}
          withError={!!errorCustomersHabilitation}
        />
        <PerformanceSummaryMetric
          label="QTD"
          value={bigNumbers?.quarterToDateValue ?? 0}
          performanceComparisonLabel="QoQ"
          performanceComparisonValue={bigNumbers?.previousQuarterValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.qoQPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersHabilitationLoading}
          withError={!!errorCustomersHabilitation}
        />
        <PerformanceSummaryMetric
          label="MTD"
          value={bigNumbers?.monthToDateValue ?? 0}
          performanceComparisonLabel="MoM"
          performanceComparisonValue={bigNumbers?.previousMonthValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.moMPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersHabilitationLoading}
          withError={!!errorCustomersHabilitation}
        />
        <PerformanceSummaryMetric
          label="WTD"
          value={bigNumbers?.weekToDateValue ?? 0}
          performanceComparisonLabel="WoW"
          performanceComparisonValue={bigNumbers?.previousWeekValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.woWPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersHabilitationLoading}
          withError={!!errorCustomersHabilitation}
        />
      </Stack>
      {charContent}
    </VStack>
  );
};
